import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Binary, Zap, CheckCircle2 } from 'lucide-react';

interface ConversionStats {
  inputLength: number;
  outputLength: number;
  processingTime: number;
  operationsCount: number;
}

interface BinaryResult {
  binary: string;
  decimal: string;
  hexadecimal: string;
  octal: string;
  ascii: string;
  utf8: string;
  stats: Omit<ConversionStats, 'operationsCount'>;
}

type InputMode = 'decimal' | 'text' | 'binary' | 'hex' | 'octal';
type OutputFormat = 'grouped' | 'continuous' | 'prefixed' | 'spaced';

const BinaryConverterTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [inputMode, setInputMode] = useState<InputMode>('decimal');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('grouped');
  const [results, setResults] = useState<BinaryResult>({
    binary: '',
    decimal: '',
    hexadecimal: '',
    octal: '',
    ascii: '',
    utf8: '',
    stats: {
      inputLength: 0,
      outputLength: 0,
      processingTime: 0
    }
  });
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [stats, setStats] = useState<ConversionStats>({
    inputLength: 0,
    outputLength: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formatBinary = useCallback((binary: string): string => {
    if (!binary) return '';
    
    switch (outputFormat) {
      case 'grouped':
        return binary.match(/.{1,8}/g)?.join(' ') || binary;
      case 'continuous':
        return binary;
      case 'prefixed':
        return `0b${binary}`;
      case 'spaced':
        return binary.split('').join(' ');
      default:
        return binary;
    }
  }, [outputFormat]);

  const convertToBinary = useCallback((input: string, mode: InputMode): BinaryResult => {
    const startTime = performance.now();
    
    if (!input.trim()) {
      return {
        binary: '',
        decimal: '',
        hexadecimal: '',
        octal: '',
        ascii: '',
        utf8: '',
        stats: {
          inputLength: 0,
          outputLength: 0,
          processingTime: 0
        }
      };
    }

    try {
      let binaryResult = '';
      let decimalResult = '';
      let hexResult = '';
      let octalResult = '';
      let asciiResult = '';
      let utf8Result = '';

      switch (mode) {
        case 'decimal':
          const num = parseInt(input.trim());
          if (isNaN(num)) {
            throw new Error('Invalid decimal number');
          }
          binaryResult = Math.abs(num).toString(2);
          if (num < 0) binaryResult = '-' + binaryResult;
          decimalResult = num.toString();
          hexResult = Math.abs(num).toString(16).toUpperCase();
          octalResult = Math.abs(num).toString(8);
          if (num >= 0 && num <= 1114111) {
            try {
              asciiResult = num <= 127 ? String.fromCharCode(num) : 'N/A';
              utf8Result = String.fromCodePoint(num);
            } catch {
              asciiResult = 'N/A';
              utf8Result = 'N/A';
            }
          }
          break;

        case 'text':
          const textBytes: number[] = [];
          const binaryParts: string[] = [];
          const hexParts: string[] = [];
          const octalParts: string[] = [];
          
          for (let i = 0; i < input.length; i++) {
            const char = input[i];
            const codePoint = char.codePointAt(0) || 0;
            
            // Handle surrogate pairs
            if (codePoint > 0xFFFF) {
              // UTF-16 surrogate pair
              const high = Math.floor((codePoint - 0x10000) / 0x400) + 0xD800;
              const low = (codePoint - 0x10000) % 0x400 + 0xDC00;
              
              textBytes.push(high, low);
              binaryParts.push(high.toString(2).padStart(16, '0'));
              binaryParts.push(low.toString(2).padStart(16, '0'));
              hexParts.push(high.toString(16).toUpperCase().padStart(4, '0'));
              hexParts.push(low.toString(16).toUpperCase().padStart(4, '0'));
              octalParts.push(high.toString(8));
              octalParts.push(low.toString(8));
              
              // Skip the next character if it's the low surrogate
              if (i + 1 < input.length && input.charCodeAt(i + 1) >= 0xDC00 && input.charCodeAt(i + 1) <= 0xDFFF) {
                i++;
              }
            } else {
              textBytes.push(codePoint);
              binaryParts.push(codePoint.toString(2).padStart(8, '0'));
              hexParts.push(codePoint.toString(16).toUpperCase().padStart(2, '0'));
              octalParts.push(codePoint.toString(8));
            }
          }
          
          binaryResult = binaryParts.join('');
          decimalResult = textBytes.join(', ');
          hexResult = hexParts.join(' ');
          octalResult = octalParts.join(' ');
          asciiResult = input;
          utf8Result = input;
          break;

        case 'binary':
          const cleanBinary = input.replace(/[^01]/g, '');
          if (!cleanBinary) {
            throw new Error('Invalid binary input');
          }
          
          binaryResult = cleanBinary;
          const decValue = parseInt(cleanBinary, 2);
          decimalResult = decValue.toString();
          hexResult = decValue.toString(16).toUpperCase();
          octalResult = decValue.toString(8);
          
          if (decValue <= 127) {
            asciiResult = String.fromCharCode(decValue);
          } else {
            asciiResult = 'N/A';
          }
          
          if (decValue <= 1114111) {
            try {
              utf8Result = String.fromCodePoint(decValue);
            } catch {
              utf8Result = 'N/A';
            }
          } else {
            utf8Result = 'N/A';
          }
          break;

        case 'hex':
          const cleanHex = input.replace(/[^0-9a-fA-F]/g, '');
          if (!cleanHex) {
            throw new Error('Invalid hexadecimal input');
          }
          
          const hexValue = parseInt(cleanHex, 16);
          binaryResult = hexValue.toString(2);
          decimalResult = hexValue.toString();
          hexResult = hexValue.toString(16).toUpperCase();
          octalResult = hexValue.toString(8);
          
          if (hexValue <= 127) {
            asciiResult = String.fromCharCode(hexValue);
          } else {
            asciiResult = 'N/A';
          }
          
          if (hexValue <= 1114111) {
            try {
              utf8Result = String.fromCodePoint(hexValue);
            } catch {
              utf8Result = 'N/A';
            }
          } else {
            utf8Result = 'N/A';
          }
          break;

        case 'octal':
          const cleanOctal = input.replace(/[^0-7]/g, '');
          if (!cleanOctal) {
            throw new Error('Invalid octal input');
          }
          
          const octValue = parseInt(cleanOctal, 8);
          binaryResult = octValue.toString(2);
          decimalResult = octValue.toString();
          hexResult = octValue.toString(16).toUpperCase();
          octalResult = octValue.toString(8);
          
          if (octValue <= 127) {
            asciiResult = String.fromCharCode(octValue);
          } else {
            asciiResult = 'N/A';
          }
          
          if (octValue <= 1114111) {
            try {
              utf8Result = String.fromCodePoint(octValue);
            } catch {
              utf8Result = 'N/A';
            }
          } else {
            utf8Result = 'N/A';
          }
          break;

        default:
          throw new Error('Invalid input mode');
      }

      const endTime = performance.now();
      const totalOutput = binaryResult.length + decimalResult.length + hexResult.length + octalResult.length;
      
      setErrorMessage('');
      
      return {
        binary: formatBinary(binaryResult),
        decimal: decimalResult,
        hexadecimal: hexResult,
        octal: octalResult,
        ascii: asciiResult,
        utf8: utf8Result,
        stats: {
          inputLength: input.length,
          outputLength: totalOutput,
          processingTime: endTime - startTime
        }
      };
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Conversion error');
      return {
        binary: '',
        decimal: '',
        hexadecimal: '',
        octal: '',
        ascii: '',
        utf8: '',
        stats: {
          inputLength: input.length,
          outputLength: 0,
          processingTime: performance.now() - startTime
        }
      };
    }
  }, [formatBinary]);

  useEffect(() => {
    const result = convertToBinary(inputText, inputMode);
    setResults(result);
    setStats(prev => ({
      ...result.stats,
      operationsCount: prev.operationsCount + (inputText.trim() ? 1 : 0)
    }));
  }, [inputText, inputMode, convertToBinary]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`);
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const content = `Binary Conversion Results
Input: ${inputText} (${inputMode.toUpperCase()})
Format: ${outputFormat}

Binary: ${results.binary}
Decimal: ${results.decimal}
Hexadecimal: ${results.hexadecimal}
Octal: ${results.octal}
ASCII: ${results.ascii}
UTF-8: ${results.utf8}

Conversion completed in ${results.stats.processingTime.toFixed(2)}ms
Input length: ${results.stats.inputLength} characters
Output length: ${results.stats.outputLength} characters
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'binary-conversion.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setResults({
      binary: '',
      decimal: '',
      hexadecimal: '',
      octal: '',
      ascii: '',
      utf8: '',
      stats: {
        inputLength: 0,
        outputLength: 0,
        processingTime: 0
      }
    });
    setStats({
      inputLength: 0,
      outputLength: 0,
      processingTime: 0,
      operationsCount: 0
    });
    setErrorMessage('');
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleText = (): void => {
    setInputText('Hello World!');
    setInputMode('text');
  };

  const loadSampleNumber = (): void => {
    setInputText('255');
    setInputMode('decimal');
  };

  const loadSampleBinary = (): void => {
    setInputText('11111111');
    setInputMode('binary');
  };

  const generateRandomBinary = (): void => {
    const length = Math.floor(Math.random() * 16) + 8; // 8-24 bits
    let binary = '';
    for (let i = 0; i < length; i++) {
      binary += Math.random() < 0.5 ? '0' : '1';
    }
    setInputText(binary);
    setInputMode('binary');
  };

  const analyzeBinary = (): void => {
    if (!results.binary) {
      showNotification('No binary data to analyze!');
      return;
    }
    
    const cleanBinary = results.binary.replace(/[^01]/g, '');
    const ones = (cleanBinary.match(/1/g) || []).length;
    const zeros = (cleanBinary.match(/0/g) || []).length;
    const ratio = zeros > 0 ? (ones / zeros).toFixed(2) : 'N/A';
    
    showNotification(`Binary Analysis: ${ones} ones, ${zeros} zeros, ratio: ${ratio}`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg">
              <Binary className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Binary Converter</h1>
              <p className="text-gray-300 text-sm">Convert between binary, decimal, text, and other number systems</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="mb-6 bg-gray-800 rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <div>
              <label className="block text-white font-medium mb-2">
                Input Data
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`Enter ${inputMode} data...`}
                className="w-full h-32 bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none font-mono text-sm resize-none"
              />
              {errorMessage && (
                <div className="mt-1 text-red-400 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="space-y-4">
              {/* Input Mode */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Input Type
                </label>
                <select
                  value={inputMode}
                  onChange={(e) => setInputMode(e.target.value as InputMode)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                >
                  <option value="decimal">Decimal Number</option>
                  <option value="text">Text (ASCII/UTF-8)</option>
                  <option value="binary">Binary</option>
                  <option value="hex">Hexadecimal</option>
                  <option value="octal">Octal</option>
                </select>
              </div>

              {/* Output Format */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Binary Format
                </label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                >
                  <option value="grouped">Grouped (8 bits)</option>
                  <option value="continuous">Continuous</option>
                  <option value="prefixed">With 0b Prefix</option>
                  <option value="spaced">Spaced</option>
                </select>
              </div>

              {/* Advanced Options */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showAdvanced}
                    onChange={(e) => setShowAdvanced(e.target.checked)}
                    className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                  />
                  <span className="text-white">Show advanced features</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Conversion Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.inputLength}</div>
                <div className="text-gray-300 text-sm">Input Length</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.outputLength}</div>
                <div className="text-gray-300 text-sm">Output Length</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Conversions</div>
              </div>
            </div>
            
            {/* Binary Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Binary System Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Base:</strong> 2 (uses digits 0 and 1)
                </div>
                <div className="text-gray-300">
                  <strong>Byte:</strong> 8 bits (e.g., 11010110)
                </div>
                <div className="text-gray-300">
                  <strong>Word:</strong> 16/32/64 bits typical
                </div>
                <div className="text-gray-300">
                  <strong>ASCII:</strong> 7-bit encoding (0-127)
                </div>
                <div className="text-gray-300">
                  <strong>UTF-8:</strong> Variable length encoding
                </div>
                <div className="text-gray-300">
                  <strong>Range:</strong> Supports large numbers
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div className="flex-1 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Conversion Results</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Binary Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Binary</h3>
                <button
                  onClick={() => copyToClipboard(results.binary, 'Binary')}
                  disabled={!results.binary}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.binary ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 2:</div>
                    <div className="text-green-400">{results.binary}</div>
                    <div className="text-gray-400 text-xs mt-1">
                      Length: {results.binary.replace(/[^01]/g, '').length} bits
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter data to see binary conversion</div>
                )}
              </div>
            </div>

            {/* Decimal Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Decimal</h3>
                <button
                  onClick={() => copyToClipboard(results.decimal, 'Decimal')}
                  disabled={!results.decimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.decimal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 10:</div>
                    <div className="text-blue-400">{results.decimal}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter data to see decimal conversion</div>
                )}
              </div>
            </div>

            {/* Hexadecimal Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Hexadecimal</h3>
                <button
                  onClick={() => copyToClipboard(results.hexadecimal, 'Hexadecimal')}
                  disabled={!results.hexadecimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.hexadecimal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 16:</div>
                    <div className="text-yellow-400">{results.hexadecimal}</div>
                    <div className="text-gray-400 text-xs mt-1">0x{results.hexadecimal}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter data to see hex conversion</div>
                )}
              </div>
            </div>

            {/* Octal Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Octal</h3>
                <button
                  onClick={() => copyToClipboard(results.octal, 'Octal')}
                  disabled={!results.octal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.octal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 8:</div>
                    <div className="text-orange-400">{results.octal}</div>
                    <div className="text-gray-400 text-xs mt-1">0o{results.octal}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter data to see octal conversion</div>
                )}
              </div>
            </div>

            {/* ASCII Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">ASCII</h3>
                <button
                  onClick={() => copyToClipboard(results.ascii, 'ASCII')}
                  disabled={!results.ascii || results.ascii === 'N/A'}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.ascii ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">ASCII Character:</div>
                    <div className="text-cyan-400">{results.ascii}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter data to see ASCII conversion</div>
                )}
              </div>
            </div>

            {/* UTF-8 Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">UTF-8</h3>
                <button
                  onClick={() => copyToClipboard(results.utf8, 'UTF-8')}
                  disabled={!results.utf8 || results.utf8 === 'N/A'}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                  <div>
                    <div className="text-gray-400 text-xs mb-1">UTF-8 Character:</div>
                    <div className="text-purple-400">{results.utf8}</div>
                  </div>
                {/* ) : (
                  <div className="text-gray-500">Enter data to see UTF-8 conversion</div>
                )} */}
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Features */}
        {showAdvanced && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Advanced Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={analyzeBinary}
                disabled={!results.binary}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
              >
                Analyze Binary
              </button>
              <button
                onClick={() => {
                  if (results.binary) {
                    const clean = results.binary.replace(/[^01]/g, '');
                    const reversed = clean.split('').reverse().join('');
                    copyToClipboard(reversed, 'Reversed Binary');
                  }
                }}
                disabled={!results.binary}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
              >
                Reverse Binary
              </button>
              <button
                onClick={() => {
                  if (results.binary) {
                    const clean = results.binary.replace(/[^01]/g, '');
                    const inverted = clean.split('').map(bit => bit === '0' ? '1' : '0').join('');
                    copyToClipboard(formatBinary(inverted), 'Inverted Binary');
                  }
                }}
                disabled={!results.binary}
                className="px-4 py-2 bg-pink-600 hover:bg-pink-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
              >
                Invert Bits
              </button>
              <button
                onClick={() => {
                  if (results.decimal && !isNaN(parseInt(results.decimal))) {
                    const num = parseInt(results.decimal);
                    const twosComplement = (~num + 1).toString(2);
                    copyToClipboard(formatBinary(twosComplement), "Two's Complement");
                  }
                }}
                disabled={!results.decimal || isNaN(parseInt(results.decimal))}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
              >
                Two's Complement
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleText}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Text
            </button>
            <button
              onClick={loadSampleNumber}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Number
            </button>
            <button
              onClick={loadSampleBinary}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Binary
            </button>
            <button
              onClick={generateRandomBinary}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Random Binary
            </button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".txt,.bin,.dat"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
              </div>
            </label>
            <button
              onClick={downloadFile}
              disabled={!results.binary}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Results</span>
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BinaryConverterTool;